import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { ShortlistBookCallButton } from '@/modules/public-shortlist/components/ShortlistBookCallButton';
import { ShortlistCandidateList } from '@/modules/public-shortlist/components/ShortlistCandidateList';
import { ShortlistCta } from '@/modules/public-shortlist/components/ShortlistCta';
import { ShortlistListDocumentMetadata } from '@/modules/public-shortlist/components/ShortlistDocumentMetadata';
import { ShortlistHeader } from '@/modules/public-shortlist/components/ShortlistHeader';
import { ShortlistInvalidLink } from '@/modules/public-shortlist/components/ShortlistInvalidLink';
import { ShortlistPageHeader } from '@/modules/public-shortlist/components/ShortlistPageHeader';
import { ShortlistPageSkeleton } from '@/modules/public-shortlist/components/ShortlistPageSkeleton';
import {
  SHORTLIST_FOOTER_CTA_LABEL,
  SHORTLIST_MAX_WIDTH_CLASS,
} from '@/modules/public-shortlist/constants';
import { useShortlistCtaReveal } from '@/modules/public-shortlist/hooks/useShortlistCtaReveal';
import { useShortlistRouteParams } from '@/modules/public-shortlist/hooks/useShortlistRouteParams';
import { useShortlistScrollPersistence } from '@/modules/public-shortlist/hooks/useShortlistScrollPersistence';
import { useShortlistTracking } from '@/modules/public-shortlist/hooks/useShortlistTracking';
import { fromUrlSlug } from '@/modules/public-shortlist/publicShortlist';
import { usePublicJobCandidatesQuery } from '@/modules/public-shortlist/queries/usePublicJobCandidatesQuery';
import { usePublicJobQuery } from '@/modules/public-shortlist/queries/usePublicJobQuery';
import { getShortlistRevealTransition } from '@/modules/public-shortlist/shortlistMotion';
import { cn } from '@/lib/shadcn/utils';

export function FreeShortlist() {
  const { company, params, roleName } = useShortlistRouteParams();
  const shortlistKey = params.isValid ? params.code : '';

  const { sentinelRef, ctaRevealed } = useShortlistCtaReveal(shortlistKey);
  const prefersReducedMotion = useReducedMotion();
  const jobQuery = usePublicJobQuery(params.isValid ? params.code : '');
  const candidatesQuery = usePublicJobCandidatesQuery(
    params.isValid ? params.code : '',
  );
  const { trackBookCallClick } = useShortlistTracking({
    shortlistCode: shortlistKey,
    enabled: params.isValid && candidatesQuery.isSuccess,
  });

  useShortlistScrollPersistence(
    shortlistKey,
    params.isValid && candidatesQuery.isSuccess,
  );

  if (!params.isValid) {
    return <ShortlistInvalidLink />;
  }

  if (candidatesQuery.isPending) {
    return <ShortlistPageSkeleton />;
  }

  if (candidatesQuery.isError) {
    return <ShortlistInvalidLink />;
  }

  const candidates = candidatesQuery.data ?? [];
  const companyName = jobQuery.data?.companyName ?? fromUrlSlug(company ?? '');
  const jobTitle = jobQuery.data?.title ?? fromUrlSlug(roleName ?? '');
  const location = jobQuery.data?.location;

  return (
    <div className="bg-neutral-120 flex min-h-screen flex-col">
      <ShortlistListDocumentMetadata
        companyName={companyName}
        jobTitle={jobTitle}
      />
      <ShortlistHeader
        onCtaClick={trackBookCallClick}
        showCta
        contentClassName={SHORTLIST_MAX_WIDTH_CLASS}
      />

      <main className="flex-1">
        <div
          className={cn(
            'mx-auto w-full px-4 py-10 sm:px-6 md:py-14 lg:px-8',
            SHORTLIST_MAX_WIDTH_CLASS,
          )}
        >
          <ShortlistPageHeader
            companyName={companyName}
            count={candidates.length}
            jobTitle={jobTitle}
            location={location}
          />

          <ShortlistCandidateList
            candidates={candidates}
            shortlistKey={shortlistKey}
            firstCardRef={sentinelRef}
          />
        </div>

        <div
          className={cn(
            'mx-auto w-full px-4 py-10 sm:px-6 md:pb-14 lg:px-8',
            SHORTLIST_MAX_WIDTH_CLASS,
          )}
        >
          <ShortlistCta onCtaClick={trackBookCallClick} />
        </div>
      </main>

      <AnimatePresence>
        {ctaRevealed ? (
          <motion.div
            key="shortlist-mobile-cta"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? undefined : { opacity: 0, y: 8 }}
            transition={getShortlistRevealTransition(prefersReducedMotion)}
            className="border-border sticky bottom-0 z-40 border-t bg-white p-4 sm:hidden"
          >
            <ShortlistBookCallButton
              className="w-full"
              label={SHORTLIST_FOOTER_CTA_LABEL}
              onClick={trackBookCallClick}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
